import { useEffect, useRef, useState } from 'react'
import { hasNativeDetector, readCode, startCamera, stopCamera } from '../lib/scanner'

/**
 * Kameraet der læser en kode - QR fra vores egne mærkater eller stregkode fra
 * leverandøren (docs/DECISIONS.md beslutning 069).
 *
 * <b>Der vælges ikke slags.</b> Montøren holder telefonen op mod noget; hvad det
 * var, finder systemet selv ud af. Skulle han først vælge "QR" eller
 * "stregkode", ville han vælge forkert halvdelen af gangene og tro, at
 * scanneren ikke virkede.
 *
 * Feltet til at taste koden står altid under kameraet. Et mærkat i en varevogn
 * bliver snavset, og en æske kan være mast - så nytter kameraet ikke noget.
 */
export function CodeScanner({
  onCode,
  busy,
  hint,
  barcodes = true
}: {
  /** Kaldes med det læste. Scanneren stopper selv først. */
  onCode: (code: string) => void
  /** Sæt til true mens opslaget kører, så der ikke scannes videre oveni. */
  busy?: boolean
  hint?: string
  /** Skal der også læses stregkoder? Falsk sparer hentningen af ZXing. */
  barcodes?: boolean
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const runningRef = useRef(false)

  const [scanning, setScanning] = useState(false)
  const [manual, setManual] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Kameraet skal slukkes, naar man forlader siden - ellers bliver lampen ved
  // med at lyse, og batteriet loeber ud i lommen.
  useEffect(() => {
    return () => {
      runningRef.current = false
      stopCamera(videoRef.current)
    }
  }, [])

  async function start() {
    setError(null)

    try {
      const video = videoRef.current
      if (!video) return

      await startCamera(video)
      setScanning(true)
      runningRef.current = true
      void loop()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kameraet kunne ikke startes.')
    }
  }

  function stop() {
    runningRef.current = false
    stopCamera(videoRef.current)
    setScanning(false)
  }

  async function loop() {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return

    const context = canvas.getContext('2d', { willReadFrequently: true })
    if (!context) return

    while (runningRef.current) {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        context.drawImage(video, 0, 0, canvas.width, canvas.height)

        const found = await readCode(canvas, barcodes)
        if (found) {
          stop()
          onCode(found.text)
          return
        }
      }

      // ~8 billeder i sekundet. Nok til at ramme en kode, man holder stille,
      // og skaansomt nok til at telefonen ikke bliver varm.
      await new Promise((resolve) => setTimeout(resolve, 120))
    }
  }

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="scanner" hidden={!scanning}>
        <video ref={videoRef} className="scanner__video" playsInline muted />
        <div className="scanner__frame" />
      </div>
      <canvas ref={canvasRef} hidden />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {scanning ? (
          <button className="btn" onClick={stop}>
            Stop kameraet
          </button>
        ) : (
          <button className="btn btn--primary" onClick={() => void start()} disabled={busy}>
            Scan
          </button>
        )}

        {hint && !scanning && <span className="tiny muted">{hint}</span>}
      </div>

      {scanning && (
        <div className="tiny muted">
          Hold kameraet mod QR-koden eller stregkoden.
          {!hasNativeDetector() && barcodes && ' Stregkoder kan tage et øjeblik på denne telefon.'}
        </div>
      )}

      <form
        className="row"
        style={{ gap: 8 }}
        onSubmit={(event) => {
          event.preventDefault()
          const value = manual.trim()
          if (!value) return

          setManual('')
          onCode(value)
        }}
      >
        <input
          style={{ maxWidth: 240 }}
          placeholder="Eller tast koden"
          value={manual}
          onChange={(event) => setManual(event.target.value)}
        />
        <button className="btn btn--sm" type="submit" disabled={!manual.trim() || busy}>
          Slå op
        </button>
      </form>

      {error && <div className="error-box">{error}</div>}
    </div>
  )
}
